import Controller from '@ember/controller';
import { action } from '@ember/object';
import { service } from '@ember/service';
import type { DataSet } from '@vdi/walden-shared/modifiers/chart';
import type { IntlService } from 'ember-intl';
import type {
  AlertType,
  ShipmentStateEnum,
  TemperatureMeasurement,
} from 'graphql-models';
import { SHIPMENT_STATE_TRANSLATIONS } from 'walden-fast/constants/state-translations';
import type { AppServicesWaldenBoxDetailModel } from 'walden-fast/routes/app/services/walden-box/detail';
import type ModalService from 'walden-fast/services/modal';

export default class AppServicesWaldenBoxDetailController extends Controller {
  declare model: AppServicesWaldenBoxDetailModel;

  @service('intl') declare intlService: IntlService;
  @service('modal') declare modalService: ModalService;

  picturesModal = 'pictures';

  stateLabel = (state: string) => {
    return this.intlService.t(
      SHIPMENT_STATE_TRANSLATIONS[state.toUpperCase() as ShipmentStateEnum],
    );
  };

  get temperatureBreachError() {
    return this.model.shipment?.alerts?.some(
      (alert) => alert.type === ('TEMPERATURE_EXCEEDED' as AlertType),
    );
  }

  get today() {
    // This is for the box status chip, the box status is always the current date (not coming from the backend)
    return new Date().toISOString();
  }

  get minimumMeasuredTemperature() {
    return Math.min(
      ...(this.model.shipment?.temperatureMeasurements?.map(
        (measurement) => measurement.valueCelsius,
      ) || []),
    );
  }

  get maximumMeasuredTemperature() {
    return Math.max(
      ...(this.model.shipment?.temperatureMeasurements?.map(
        (measurement) => measurement.valueCelsius,
      ) || []),
    );
  }

  get temperatureDataSet(): DataSet {
    return {
      error: !!this.temperatureBreachError,
      label: this.intlService.t(
        'pages.waldenBoxDetail.temperature.chart.label',
      ),
      data:
        this.model.shipment?.temperatureMeasurements?.map(
          (measurement: TemperatureMeasurement) => ({
            key: `${this.intlService.formatDate(measurement.measuredAt)}  ${this.intlService.formatTime(measurement.measuredAt)}`,
            value: parseFloat(measurement.valueCelsius.toFixed(2)),
          }),
        ) || [],
    };
  }

  get temperatureDataSetMax(): number {
    return Math.ceil(
      Math.max(
        ...this.temperatureDataSet.data.map((measurement) => measurement.value),
        this.model.shipment?.maxTemperature as number,
      ),
    );
  }

  get temperatureDataSetMin(): number {
    return Math.floor(
      Math.min(
        ...this.temperatureDataSet.data.map((measurement) => measurement.value),
        this.model.shipment?.minTemperature as number,
      ),
    );
  }

  get temperatureAnnotationLines() {
    return [
      {
        label: this.intlService.t(
          'pages.waldenBoxDetail.temperature.chart.max',
          { value: this.model.shipment?.maxTemperature },
        ),
        value: this.model.shipment?.maxTemperature as number,
      },
      {
        label: this.intlService.t(
          'pages.waldenBoxDetail.temperature.chart.min',
          { value: this.model.shipment?.minTemperature },
        ),
        value: this.model.shipment?.minTemperature as number,
      },
    ];
  }

  get routeCoordinates() {
    return (
      (this.model.shipment?.locationMeasurements?.map(
        ({ latitude, longitude }) => [longitude, latitude],
      ) as [number, number][]) || []
    );
  }

  get pictures() {
    if (
      !this.model.shipment?.pictures ||
      this.model.shipment?.pictures?.length === 0
    ) {
      return [];
    }

    return this.model.shipment.pictures.map((picture) => {
      return {
        src: picture.bitmapUrl,
        alt: this.intlService.t(
          'pages.waldenBoxDetail.pictures.modal.pictureAlt',
          { value: picture.id },
        ),
        title: picture.id,
        timestamp: new Date(picture.createdAt),
      };
    });
  }

  @action
  openPicturesModal() {
    this.modalService.open(this.picturesModal);
  }
}
